import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../services/supabase.ts';
import { Tenant } from '../../types/tenant.ts';
import { Property } from '../../types/property.ts';
import DocumentUpload from './DocumentUpload.tsx';
import { formatCurrency } from '../../utils/formatters.ts';

interface TenantFormProps {
  tenant?: Tenant | null;
  onSubmit: (data: Omit<Tenant, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onClose: () => void;
}

const TenantForm: React.FC<TenantFormProps> = ({ tenant, onSubmit, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);

  const calculateEndDate = (startDate: string): string => {
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + 11);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    full_name: tenant?.full_name || '',
    phone: tenant?.phone || '',
    email: tenant?.email || '',
    emergency_contact: tenant?.emergency_contact || '',
    aadhar_no: tenant?.aadhar_no || '',
    pan_no: tenant?.pan_no || '',
    start_date: tenant?.start_date || new Date().toISOString().split('T')[0],
    end_date: tenant?.end_date || calculateEndDate(new Date().toISOString().split('T')[0]),
    monthly_rent: tenant?.monthly_rent || 0,
    security_deposit: tenant?.security_deposit || 0,
    payment_due_date: tenant?.payment_due_date || 5,
    property_id: tenant?.property_id || '',
    notes: tenant?.notes || '',
    status: tenant?.status || 'Active'
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [wasSubmitAttempted, setWasSubmitAttempted] = useState(false);

  const AMOUNT_LIMIT = 1000000;

  const fetchAvailableProperties = useCallback(async () => {
    try {
      const { data: allProperties } = await supabase
        .from('properties')
        .select(`
          *,
          tenants (
            id,
            status
          )
        `);

      if (!allProperties) return;

      const availableProperties = allProperties.filter(property => {
        if (tenant?.status === 'Active') {
          return property.id === tenant.property_id;
        }

        if (property.status === 'Vacant') {
          return true;
        }

        if (tenant && property.id === tenant.property_id) {
          const hasOtherActiveTenant = property.tenants?.some(t => 
            t.status === 'Active' && t.id !== tenant.id
          );
          return !hasOtherActiveTenant;
        }

        return false;
      });

      setProperties(availableProperties);
    } catch (error) {
      console.error('Error fetching properties:', error);
      alert('Error loading properties');
    }
  }, [tenant]);

  useEffect(() => {
    fetchAvailableProperties();
  }, [fetchAvailableProperties]);

  const validatePhone = (phone: string) => {
    return phone.replace(/\D/g, '').slice(0, 10);
  };

  const validateAadhar = (aadhar: string) => {
    return aadhar.replace(/\D/g, '').slice(0, 12);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.full_name.trim()) errors.full_name = 'This field is required';
    if (!formData.phone.trim()) errors.phone = 'This field is required';
    if (!formData.property_id) errors.property_id = 'This field is required';
    if (!formData.monthly_rent || formData.monthly_rent <= 0) errors.monthly_rent = 'This field is required';
    if (!formData.security_deposit || formData.security_deposit <= 0) errors.security_deposit = 'This field is required';
    if (!formData.payment_due_date) errors.payment_due_date = 'This field is required';
    if (!formData.start_date) errors.start_date = 'This field is required';
    if (!formData.end_date) errors.end_date = 'This field is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearError = (field: string) => {
    if (wasSubmitAttempted) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      start_date: newStartDate,
      // Only auto-set end date if it's empty or invalid
      end_date: !prev.end_date ? calculateEndDate(newStartDate) : prev.end_date
    }));
    clearError('start_date');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      end_date: newEndDate
    }));
    clearError('end_date');
  };

  const handleMonthlyRentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*$/.test(value)) {
      const numValue = Math.min(parseInt(value) || 0, AMOUNT_LIMIT);
      setFormData(prev => ({
        ...prev,
        monthly_rent: numValue
      }));
      clearError('monthly_rent');
    }
  };

  const handleSecurityDepositChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*$/.test(value)) {
      const numValue = Math.min(parseInt(value) || 0, AMOUNT_LIMIT);
      setFormData(prev => ({
        ...prev,
        security_deposit: numValue
      }));
      clearError('security_deposit');
    }
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 50) {
      setFormData({ ...formData, notes: text });
    }
  };

  const handlePaymentDueDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (/^\d*$/.test(value))) {
      const numValue = parseInt(value) || 0;
      if (numValue <= 31) {
        setFormData({ ...formData, payment_due_date: numValue });
        clearError('payment_due_date');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWasSubmitAttempted(true);
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      
      if (!tenant && formData.property_id) {
        await supabase
          .from('properties')
          .update({ status: 'Rented' })
          .eq('id', formData.property_id);
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting tenant:', error);
      alert('Failed to save tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleModalWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Add new effect to update monthly rent when property is selected
  useEffect(() => {
    if (formData.property_id) {
      const selectedProperty = properties.find(p => p.id === formData.property_id);
      if (selectedProperty) {
        setFormData(prev => ({
          ...prev,
          monthly_rent: selectedProperty.planned_rent
        }));
      }
    }
  }, [formData.property_id, properties]);

  const handlePropertyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const propertyId = e.target.value;
    const selectedProperty = properties.find(p => p.id === propertyId);
    
    setFormData(prev => ({
      ...prev,
      property_id: propertyId,
      monthly_rent: selectedProperty ? selectedProperty.planned_rent : prev.monthly_rent
    }));
  };

  return (
    <div className="modal" onClick={onClose}>
      <div 
        className="tenant-form" 
        onClick={(e) => e.stopPropagation()}
        onWheel={handleModalWheel}
      >
        <div className="modal-header">
          <h2>{tenant ? 'Edit' : 'Add New'} Tenant</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-grid">
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => {
                  if (e.target.value.length <= 50) {
                    setFormData({ ...formData, full_name: e.target.value });
                  }
                  clearError('full_name');
                }}
                maxLength={50}
                required
              />
              {validationErrors.full_name && (
                <span className="validation-message">{validationErrors.full_name}</span>
              )}
            </div>

            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  setFormData({ 
                    ...formData, 
                    phone: validatePhone(e.target.value)
                  });
                  clearError('phone');
                }}
                pattern="[0-9]{10}"
                maxLength={10}
                required
              />
              {validationErrors.phone && (
                <span className="validation-message">{validationErrors.phone}</span>
              )}
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
            </div>

            <div className="form-group">
              <label>Emergency Contact</label>
              <input
                type="tel"
                value={formData.emergency_contact}
                onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Aadhar Number</label>
              <input
                type="text"
                value={formData.aadhar_no}
                onChange={(e) => {
                  setFormData({ 
                    ...formData, 
                    aadhar_no: validateAadhar(e.target.value)
                  });
                  clearError('aadhar_no');
                }}
                pattern="[0-9]{12}"
                maxLength={12}
              />
              {validationErrors.aadhar_no && (
                <span className="validation-message">{validationErrors.aadhar_no}</span>
              )}
            </div>

            <div className="form-group">
              <label>PAN Number</label>
              <input
                type="text"
                value={formData.pan_no}
                onChange={(e) => setFormData({ ...formData, pan_no: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Property *</label>
              <select
                value={formData.property_id}
                onChange={handlePropertyChange}
                required
                disabled={tenant?.status === 'Active'}
              >
                <option value="">Select Property</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name}
                  </option>
                ))}
              </select>
              {validationErrors.property_id && (
                <span className="validation-message">{validationErrors.property_id}</span>
              )}
            </div>

            <div className="form-group">
              <label>Monthly Rent (₹) *</label>
              <input
                type="text"
                value={formData.monthly_rent}
                onChange={handleMonthlyRentChange}
                min="1"
                max={AMOUNT_LIMIT}
                // disabled={!!tenant}
                title={tenant ? "Monthly rent cannot be edited" : ""}
              />
              {formData.property_id && (
                <span className="form-hint">
                  Suggested rent based on property: {formatCurrency(properties.find(p => p.id === formData.property_id)?.planned_rent || 0)}
                </span>
              )}
              {formData.monthly_rent >= AMOUNT_LIMIT && (
                <span className="validation-message">
                  Amount cannot exceed {formatCurrency(AMOUNT_LIMIT)}
                </span>
              )}
              {validationErrors.monthly_rent && (
                <span className="validation-message">{validationErrors.monthly_rent}</span>
              )}
            </div>

            <div className="form-group">
              <label>Security Deposit (₹) *</label>
              <input
                type="text"
                value={formData.security_deposit}
                onChange={handleSecurityDepositChange}
                min="1"
                // disabled={!!tenant}
                title={tenant ? "Security deposit cannot be edited" : ""}
              />
              <span className="form-hint">
                Suggested: {formatCurrency(formData.monthly_rent)} (monthly rent)
              </span>
              {validationErrors.security_deposit && (
                <span className="validation-message">{validationErrors.security_deposit}</span>
              )}
            </div>

            <div className="form-group">
              <label>Payment Due Date *</label>
              <input
                type="text"
                value={formData.payment_due_date}
                onChange={handlePaymentDueDateChange}
                placeholder="Enter day (1-31)"
                maxLength={2}
                required
              />
              {validationErrors.payment_due_date && (
                <span className="validation-message">{validationErrors.payment_due_date}</span>
              )}
            </div>

            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={handleStartDateChange}
                max="9999-12-31"
                required
              />
              {validationErrors.start_date && (
                <span className="validation-message">{validationErrors.start_date}</span>
              )}
            </div>

            <div className="form-group">
              <label>End Date *</label>
              <input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleEndDateChange}
                min={formData.start_date}
                max="9999-12-31"
                required
              />
              {validationErrors.end_date && (
                <span className="validation-message">{validationErrors.end_date}</span>
              )}
            </div>
          
          <div className="form-group">
          <label>Notes</label>
              <textarea
                value={formData.notes}
                onChange={handleNotesChange}
                rows={3}
                maxLength={50}
              />
              <span className="form-hint">
                {`${formData.notes.length} of 50 characters`}
              </span>
            </div>
          </div>
          {tenant && (
            <div className="form-section">
              <h3>Documents</h3>
              <DocumentUpload tenantId={tenant.id} />
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (tenant ? 'Update' : 'Add')} Tenant
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantForm;